import React, { useState } from 'react';
import { Box, Typography, Button, Grid, TextField, FormControlLabel, Checkbox, CircularProgress, Paper, Alert, Divider, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Check, DollarSign, CreditCard, Zap, User, Bot, X } from 'lucide-react';

interface PaymentPanelProps {
  proposalId: string | null;
  solutions: any[];
  onPaymentComplete?: () => void;
}

const PaymentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const PaymentMethod = styled(Paper)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 30px rgba(138, 43, 226, 0.3)',
    border: '1px solid rgba(138, 43, 226, 0.5)',
  },
}));

const PaymentSummaryItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 0),
}));

const StepsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing(3),
}));

const Step = styled(Box)<{ active: boolean }>(({ theme, active }) => ({
  flex: 1,
  textAlign: 'center',
  padding: theme.spacing(1),
  borderBottom: active 
    ? '2px solid #00CC99' 
    : '2px solid rgba(138, 43, 226, 0.2)',
  color: active ? '#00CC99' : 'rgba(255, 255, 255, 0.5)',
}));

const ContributorItem = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const PaymentPanel: React.FC<PaymentPanelProps> = ({ proposalId, solutions, onPaymentComplete }) => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [paymentMethod, setPaymentMethod] = useState<string>('card');
  const [loading, setLoading] = useState<boolean>(false);
  const [paymentComplete, setPaymentComplete] = useState<boolean>(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  
  const [paymentDistribution, setPaymentDistribution] = useState([
    { id: '1', name: 'Jane Smith', role: 'Problem Creator', type: 'human', percentage: 30 },
    { id: '2', name: 'AI Assistant', role: 'Solution Provider', type: 'ai', percentage: 50 },
    { id: '3', name: 'Platform Fee', role: 'Service Fee', type: 'system', percentage: 20 }
  ]);
  
  // Get the accepted solution
  const acceptedSolution = solutions.find(s => s.status === 'accepted') || solutions[0];
  const solutionCost = acceptedSolution?.estimatedCost || 10000;

  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handlePaymentMethodSelect = (method: string) => {
    setPaymentMethod(method);
  };

  const handlePercentageChange = (id: string, value: string) => {
    const numValue = parseInt(value) || 0;
    
    const updatedDistribution = paymentDistribution.map(item => 
      item.id === id ? { ...item, percentage: numValue } : item
    );
    
    // Ensure total is 100%
    let total = updatedDistribution.reduce((sum, item) => sum + item.percentage, 0);
    
    // If total is not 100%, adjust the platform fee
    if (total !== 100) {
      const platformFee = updatedDistribution.find(item => item.type === 'system');
      if (platformFee) {
        platformFee.percentage = Math.max(5, 100 - (total - platformFee.percentage));
      }
    }
    
    setPaymentDistribution(updatedDistribution);
  };

  const handleCompletePayment = () => {
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      // Add a small chance of payment failure for demonstration
      if (Math.random() < 0.1) {
        setLoading(false);
        setPaymentError("Payment processing failed. Please try again or use a different payment method.");
      } else {
        setLoading(false);
        setPaymentComplete(true);
        
        // Call the onPaymentComplete callback if provided
        if (onPaymentComplete) {
          onPaymentComplete();
        }
      }
    }, 2000);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getPaymentStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
              Select Payment Method
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <PaymentMethod 
                  onClick={() => handlePaymentMethodSelect('card')}
                  sx={{
                    borderColor: paymentMethod === 'card' ? '#00CC99' : 'rgba(138, 43, 226, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CreditCard size={32} color={paymentMethod === 'card' ? '#00CC99' : '#8A2BE2'} />
                    <Typography variant="subtitle1" sx={{ mt: 1, color: '#fff' }}>
                      Credit Card
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', mt: 1 }}>
                      Pay with Visa, Mastercard, or American Express
                    </Typography>
                  </Box>
                </PaymentMethod>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <PaymentMethod 
                  onClick={() => handlePaymentMethodSelect('crypto')}
                  sx={{
                    borderColor: paymentMethod === 'crypto' ? '#00CC99' : 'rgba(138, 43, 226, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Zap size={32} color={paymentMethod === 'crypto' ? '#00CC99' : '#8A2BE2'} />
                    <Typography variant="subtitle1" sx={{ mt: 1, color: '#fff' }}>
                      Cryptocurrency
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', mt: 1 }}>
                      Pay with Bitcoin, Ethereum, or USDC
                    </Typography>
                  </Box>
                </PaymentMethod>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <PaymentMethod 
                  onClick={() => handlePaymentMethodSelect('balance')}
                  sx={{
                    borderColor: paymentMethod === 'balance' ? '#00CC99' : 'rgba(138, 43, 226, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <DollarSign size={32} color={paymentMethod === 'balance' ? '#00CC99' : '#8A2BE2'} />
                    <Typography variant="subtitle1" sx={{ mt: 1, color: '#fff' }}>
                      Account Balance
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', mt: 1 }}>
                      Use your existing account balance
                    </Typography>
                  </Box>
                </PaymentMethod>
              </Grid>
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button variant="outlined" disabled sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNextStep}
                sx={{
                  background: 'linear-gradient(45deg, #00CC99 30%, #008B8B 90%)',
                  color: '#fff',
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        );
        
      case 2:
        return (
          <Box>
            <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
              Payment Attribution
            </Typography>
            
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
              Specify how you would like to distribute the payment to contributors.
            </Typography>
            
            {paymentDistribution.map((contributor) => (
              <ContributorItem key={contributor.id}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {contributor.type === 'human' ? (
                    <User size={24} color="#00CC99" style={{ marginRight: 12 }} />
                  ) : contributor.type === 'ai' ? (
                    <Bot size={24} color="#8A2BE2" style={{ marginRight: 12 }} />
                  ) : (
                    <Zap size={24} color="#FF6F00" style={{ marginRight: 12 }} />
                  )}
                  
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#fff' }}>
                      {contributor.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      {contributor.role}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    type="number"
                    value={contributor.percentage}
                    onChange={(e) => handlePercentageChange(contributor.id, e.target.value)}
                    inputProps={{ min: 0, max: 100, step: 5 }}
                    disabled={contributor.type === 'system'}
                    sx={{
                      width: 80,
                      input: { color: '#fff', textAlign: 'right' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(138, 43, 226, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(138, 43, 226, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#8a2be2',
                        },
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ ml: 1, color: '#fff', width: 40 }}>%</Typography>
                </Box>
              </ContributorItem>
            ))}
            
            <Alert severity="info" sx={{ mt: 2, mb: 3, background: 'rgba(33, 150, 243, 0.1)', color: '#fff' }}>
              The Platform Fee is fixed at minimum 5% to maintain the platform services.
            </Alert>
            
            <Typography variant="h6" sx={{ color: '#fff', mt: 4, mb: 2 }}>
              Payment Summary
            </Typography>
            
            <Box sx={{ background: 'rgba(26, 26, 46, 0.6)', borderRadius: 2, p: 2, mb: 3 }}>
              <PaymentSummaryItem>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Solution Cost:
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  {formatCurrency(solutionCost)}
                </Typography>
              </PaymentSummaryItem>
              
              <Divider sx={{ my: 1, borderColor: 'rgba(138, 43, 226, 0.2)' }} />
              
              {paymentDistribution.map((contributor) => (
                <PaymentSummaryItem key={contributor.id}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {contributor.name} ({contributor.percentage}%):
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#fff' }}>
                    {formatCurrency(solutionCost * (contributor.percentage / 100))}
                  </Typography>
                </PaymentSummaryItem>
              ))}
              
              <Divider sx={{ my: 1, borderColor: 'rgba(138, 43, 226, 0.2)' }} />
              
              <PaymentSummaryItem>
                <Typography variant="subtitle2" sx={{ color: '#00CC99' }}>
                  Total:
                </Typography>
                <Typography variant="subtitle2" sx={{ color: '#00CC99' }}>
                  {formatCurrency(solutionCost)}
                </Typography>
              </PaymentSummaryItem>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button 
                variant="outlined" 
                onClick={handlePreviousStep}
                sx={{ 
                  borderColor: 'rgba(138, 43, 226, 0.5)',
                  color: '#fff',
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNextStep}
                sx={{
                  background: 'linear-gradient(45deg, #00CC99 30%, #008B8B 90%)',
                  color: '#fff',
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        );
        
      case 3:
        return (
          <Box>
            <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
              Confirm and Pay
            </Typography>
            
            {paymentMethod === 'card' && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ color: '#fff', mb: 2 }}>
                  Card Details
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Card Number"
                      placeholder="1234 5678 9012 3456"
                      variant="outlined"
                      InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                      sx={{
                        input: { color: '#fff' },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'rgba(138, 43, 226, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(138, 43, 226, 0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#8a2be2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Expiration Date"
                      placeholder="MM/YY"
                      variant="outlined"
                      InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                      sx={{
                        input: { color: '#fff' },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'rgba(138, 43, 226, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(138, 43, 226, 0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#8a2be2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="CVV"
                      placeholder="123"
                      variant="outlined"
                      InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                      sx={{
                        input: { color: '#fff' },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'rgba(138, 43, 226, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(138, 43, 226, 0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#8a2be2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          defaultChecked 
                          sx={{
                            color: 'rgba(138, 43, 226, 0.5)',
                            '&.Mui-checked': {
                              color: '#8A2BE2',
                            },
                          }}
                        />
                      }
                      label="Save card for future payments"
                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
            
            {paymentMethod === 'crypto' && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ color: '#fff', mb: 2 }}>
                  Cryptocurrency Payment
                </Typography>
                
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                  Select cryptocurrency:
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <PaymentMethod 
                      sx={{
                        borderColor: '#00CC99',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 1
                      }}
                    >
                      <Chip label="BTC" sx={{ background: '#F7931A', color: '#fff', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: '#fff' }}>
                        Bitcoin
                      </Typography>
                    </PaymentMethod>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <PaymentMethod 
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 1
                      }}
                    >
                      <Chip label="ETH" sx={{ background: '#627EEA', color: '#fff', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: '#fff' }}>
                        Ethereum
                      </Typography>
                    </PaymentMethod>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <PaymentMethod 
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 1
                      }}
                    >
                      <Chip label="USDC" sx={{ background: '#2775CA', color: '#fff', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: '#fff' }}>
                        USD Coin
                      </Typography>
                    </PaymentMethod>
                  </Grid>
                </Grid>
                
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 3, mb: 2 }}>
                  Wallet address:
                </Typography>
                
                <TextField
                  fullWidth
                  placeholder="Your cryptocurrency wallet address"
                  variant="outlined"
                  sx={{
                    input: { color: '#fff' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(138, 43, 226, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(138, 43, 226, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#8a2be2',
                      },
                    },
                  }}
                />
              </Box>
            )}
            
            {paymentMethod === 'balance' && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ color: '#fff', mb: 2 }}>
                  Account Balance
                </Typography>
                
                <Box sx={{ 
                  background: 'rgba(26, 26, 46, 0.6)', 
                  borderRadius: 2, 
                  p: 3, 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Current Balance:
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#00CC99', fontWeight: 600 }}>
                      {formatCurrency(25000)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      After Payment:
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                      {formatCurrency(25000 - solutionCost)}
                    </Typography>
                  </Box>
                </Box>
                
                {(25000 - solutionCost) < 0 && (
                  <Alert severity="error" sx={{ mt: 2, background: 'rgba(244, 67, 54, 0.1)', color: '#f44336' }}>
                    Insufficient balance. Please top up your account or choose a different payment method.
                  </Alert>
                )}
              </Box>
            )}
            
            {/* Payment Summary & Actions */}
            <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
              Payment Summary
            </Typography>
            
            <Box sx={{ background: 'rgba(26, 26, 46, 0.6)', borderRadius: 2, p: 2, mb: 4 }}>
              <Typography variant="subtitle2" sx={{ color: '#fff', mb: 1 }}>
                Solution: {acceptedSolution?.title || 'Selected Solution'}
              </Typography>
              
              <Divider sx={{ my: 1, borderColor: 'rgba(138, 43, 226, 0.2)' }} />
              
              <PaymentSummaryItem>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Total Amount:
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                  {formatCurrency(solutionCost)}
                </Typography>
              </PaymentSummaryItem>
              
              <PaymentSummaryItem>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Payment Method:
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff' }}>
                  {paymentMethod === 'card' && 'Credit Card'}
                  {paymentMethod === 'crypto' && 'Cryptocurrency'}
                  {paymentMethod === 'balance' && 'Account Balance'}
                </Typography>
              </PaymentSummaryItem>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                variant="outlined" 
                onClick={handlePreviousStep}
                sx={{ 
                  borderColor: 'rgba(138, 43, 226, 0.5)',
                  color: '#fff',
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleCompletePayment}
                disabled={loading || (paymentMethod === 'balance' && (25000 - solutionCost) < 0)}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Check size={20} />}
                sx={{
                  background: 'linear-gradient(45deg, #00CC99 30%, #008B8B 90%)',
                  color: '#fff',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #00E676 30%, #00CC99 90%)',
                  },
                }}
              >
                {loading ? 'Processing...' : 'Complete Payment'}
              </Button>
            </Box>
          </Box>
        );
        
      case 4:
        return (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            {paymentComplete ? (
              <Box>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'rgba(0, 204, 153, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                  }}
                >
                  <Check size={40} color="#00CC99" />
                </Box>
                
                <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>
                  Payment Complete!
                </Typography>
                
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 4 }}>
                  Your payment of {formatCurrency(solutionCost)} has been processed successfully.
                </Typography>
                
                <Box sx={{ background: 'rgba(26, 26, 46, 0.6)', borderRadius: 2, p: 3, mb: 4, textAlign: 'left' }}>
                  <Typography variant="subtitle2" sx={{ color: '#fff', mb: 2 }}>
                    Payment Details
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Transaction ID:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#fff' }}>
                        TXN-{Math.random().toString(36).substring(2, 10).toUpperCase()}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Date:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#fff' }}>
                        {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Amount:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#00CC99', fontWeight: 600 }}>
                        {formatCurrency(solutionCost)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Payment Method:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ color: '#fff' }}>
                        {paymentMethod === 'card' && 'Credit Card'}
                        {paymentMethod === 'crypto' && 'Cryptocurrency'}
                        {paymentMethod === 'balance' && 'Account Balance'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                
                <Button
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(45deg, #00CC99 30%, #008B8B 90%)',
                    color: '#fff',
                    px: 4,
                  }}
                >
                  View Receipt
                </Button>
              </Box>
            ) : paymentError ? (
              <Box>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'rgba(244, 67, 54, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                  }}
                >
                  <X size={40} color="#f44336" />
                </Box>
                
                <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>
                  Payment Failed
                </Typography>
                
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 4 }}>
                  {paymentError}
                </Typography>
                
                <Button
                  variant="contained"
                  onClick={handlePreviousStep}
                  sx={{
                    background: 'linear-gradient(45deg, #f44336 30%, #d32f2f 90%)',
                    color: '#fff',
                    px: 4,
                  }}
                >
                  Try Again
                </Button>
              </Box>
            ) : (
              <Box>
                <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>
                  Processing Payment
                </Typography>
                
                <CircularProgress sx={{ my: 4 }} />
                
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Please wait while we process your payment...
                </Typography>
              </Box>
            )}
          </Box>
        );
      
      default:
        return (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Invalid step
            </Typography>
          </Box>
        );
    }
  };

  const renderStepIndicators = () => {
    return (
      <StepsContainer>
        <Step active={currentStep === 1}>
          <Typography variant="body2">
            1. Method
          </Typography>
        </Step>
        <Step active={currentStep === 2}>
          <Typography variant="body2">
            2. Attribution
          </Typography>
        </Step>
        <Step active={currentStep === 3}>
          <Typography variant="body2">
            3. Review
          </Typography>
        </Step>
        <Step active={currentStep === 4}>
          <Typography variant="body2">
            4. Complete
          </Typography>
        </Step>
      </StepsContainer>
    );
  };

  // Add the render function at the end of the component
  return (
    <PaymentContainer>
      {renderStepIndicators()}
      {getPaymentStepContent()}
    </PaymentContainer>
  );
};

export default PaymentPanel;
